<template>
    <div class="admin-invite-members-form">
        <form-wrapper>
            <form-row>
                <form-cell>
                    <common-page-header
                        :heading="pageHeading"
                        size="page-plain-header"
                        contentSpacing="medium"
                        :isCenter="true"
                    ></common-page-header>
                    <radio-field
                        :isRequired="true"
                        :formData="data"
                        :options="inviteTypes"
                        valuePath="inviteType"
                        @change="change($event)"
                    ></radio-field>
                </form-cell>
            </form-row>

            <form-row v-if="data.inviteType === InviteTypeEnum.PRACTICE">
                <form-cell>
                    <suggest-field
                        :label="$t('pages.administrator.invite_members.practice_name')"
                        :placeholder="$t('pages.administrator.invite_members.practice_name')"
                        :suggestionsCall="practiceSuggestionsCall"
                        :isRequired="true"
                        :formData="data"
                        valuePath="practiceName"
                        @change="change($event)"
                        @select="updatePractice($event)"
                    ></suggest-field>
                </form-cell>
            </form-row>

            <form-row v-if="data.inviteType === InviteTypeEnum.NETWORK">
                <form-cell>
                    <suggest-field
                        :label="$t('pages.administrator.invite_members.physician')"
                        :placeholder="$t('pages.administrator.invite_members.physician')"
                        :suggestionsCall="physicianSuggestionsCall"
                        :isRequired="true"
                        :formData="data"
                        valuePath="physician"
                        @change="change($event)"
                        @select="updatePhysician($event)"
                    ></suggest-field>
                </form-cell>
            </form-row>
            <hr class="horizontal-line" />
            <form-row>
                <form-cell></form-cell>
            </form-row>
        </form-wrapper>
        <email-collector
            v-if="data.inviteType && (data.userId || data.masterUserId)"
            ref="emailCollector"
            :label="$t('pages.auth.signup.steps.register_physicians.form.field_label')"
            :placeholder="$t('pages.auth.signup.steps.register_physicians.form.field_placeholder')"
            :buttonLabel="$t('pages.auth.signup.steps.register_physicians.form.button_label')"
            :initialNumOfFields="3"
            :formData="formDataEmailFields"
            :inviteBy="inviteBy"
            valuePath="emailFields"
            @emails-collected="emailsCollected"
            @change="emailsChanged"
        ></email-collector>
        <common-modal
            v-if="showCheckInviteEmailsModal"
            :width="'medium'"
            @emitEvent="emitEvent($event, ['close'])"
            @close="emitEvent({ name: 'close' })"
        >
            <send-join-invites-overview
                :emailsCheck="emailsCheck"
                :defaultCloseButtonEnabled="true"
                practiceType="physician"
                @new-email-invitations-present="invitesSentAfterEmailCheck"
                @close="closeCheckInviteEmailsModal"
            ></send-join-invites-overview>
        </common-modal>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </div>
</template>

<script>
    import PracticesAPI from 'api/PracticesAPI'
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import AdministratorAPI from 'api/AdministratorAPI'
    import UserRolesEnum from '@/enums/UserRolesEnum'
    import UsersAPI from 'api/UsersAPI'
    import FormMixin from 'mixins/FormMixin'
    import InvitationEmailCheckModalMixin from 'mixins/InvitationEmailCheckModalMixin'
    import SendJoinInvitesOverview from 'components/page-elements/invites/SendJoinInvitesOverview'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import InviteTypeEnum from '@/enums/InviteTypeEnum'
    import EmailCollector from 'components/page-elements/network/EmailCollector'
    import InviteByEnum from '@/enums/InviteByEnum'

    export default {
        mixins: [FormMixin, InvitationEmailCheckModalMixin],

        components: { EmailCollector, SendJoinInvitesOverview },

        props: {
            inviteBy: { type: String, required: false, default: InviteByEnum.INVITE_BY_EMAIL },
        },
        data() {
            return {
                notificationMessage: '',
                isInviteErrorModalOpen: false,
                joinInviteErrorMessageCode: null,
                showCheckInviteEmailsModal: false,
                InviteTypeEnum: InviteTypeEnum,
                userId: null,
            }
        },

        computed: {
            pageHeading() {
                return this.$t('pages.administrator.invite_members.invite_by', {
                    inviteType: this.inviteBy === InviteByEnum.INVITE_BY_EMAIL ? 'Email' : 'Phone',
                })
            },
            inviteTypes() {
                return [
                    {
                        label: this.$t('pages.administrator.invite_members.invite_network'),
                        value: InviteTypeEnum.NETWORK,
                    },
                    {
                        label: this.$t('pages.administrator.invite_members.invite_practice'),
                        value: InviteTypeEnum.PRACTICE,
                    },
                ]
            },

            practiceSuggestionsCall() {
                return (query) => PracticesAPI.getList(query, PracticeTypeEnum.PHYSICIAN)
            },

            physicianSuggestionsCall() {
                return (query) => PhysiciansAPI.getUserList(query, 1)
            },
        },

        methods: {
            emailsChanged(ev) {
                this.sourceEmailFields = ev.value
            },

            emailsCollected(ev) {
                const userId = this.data.inviteType === InviteTypeEnum.NETWORK ? this.data.userId : this.data.masterUserId
                UsersAPI.checkJoinInvites(userId, ev, PracticeTypeEnum.PHYSICIAN, this.inviteBy)
                    .then((response) => {
                        this.data.userId = userId
                        if (this.inviteBy === InviteByEnum.INVITE_BY_PHONE) {
                            this.sendJoinInvites(ev)
                            return
                        }
                        if (
                            (response.emailsCheck.invited && response.emailsCheck.invited.length) ||
                            (response.emailsCheck.registeredInvites && response.emailsCheck.registeredInvites.length) ||
                            (response.emailsCheck.registeredOther && response.emailsCheck.registeredOther.length) ||
                            (response.emailsCheck.registrationNotCompleted &&
                                response.emailsCheck.registrationNotCompleted.length)
                        ) {
                            this.emailsCheck = response.emailsCheck
                            this.showCheckInviteEmailsModal = true
                        } else if (
                            response.emailsCheck.invited &&
                            response.emailsCheck.invited.length === 0 &&
                            response.emailsCheck.registeredInvites &&
                            response.emailsCheck.registeredInvites.length === 0 &&
                            response.emailsCheck.registeredOther &&
                            response.emailsCheck.registeredOther.length === 0 &&
                            response.emailsCheck.registrationNotCompleted &&
                            response.emailsCheck.registrationNotCompleted.length === 0 &&
                            response.emailsCheck.newInvitations &&
                            response.emailsCheck.newInvitations.length
                        ) {
                            this.sendJoinInvites(response.emailsCheck.newInvitations)
                        }
                    })
                    .catch((error) => {
                        this.$emit('join-invites-error', error.response)
                    })
            },

            closeCheckInviteEmailsModal() {
                this.showCheckInviteEmailsModal = false
            },

            inviteToNetwork(ev) {
                const request = {
                    practiceType: ev.practiceType,
                    invites: ev.invites,
                    userId: ev.userId,
                    userRoleId: ev.userRoleId,
                }
                AdministratorAPI.inviteToNetwork(request).then(() => {
                    this.notificationMessage = this.$t('modals.join_invites_member.invites_sent')
                    this.$refs.commonSuccessNotificationChild.showNotification()
                    this.clearForm()
                })
            },

            inviteToPractice(ev) {
                const request = {
                    practiceType: ev.practiceType,
                    invites: ev.invites,
                    userId: ev.userId,
                    practiceId: ev.practiceId,
                }
                AdministratorAPI.inviteToPractice(request)
                    .then(() => {
                        this.notificationMessage = this.$t('modals.join_invites_member.invites_sent')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                        this.clearForm()
                    })
                    .catch((error) => {
                        this.notificationMessage = error?.data[0]
                            ? error?.data[0]?.code
                            : this.$t('modals.join_invites_member.invites_sent')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
            },

            clearForm() {
                this.prefillData({
                    masterUserId: null,
                    practiceId: null,
                    userId: null,
                    practiceName: null,
                    physician: null,
                })
                this.sourceEmailFields = this.resetEmails()
            },

            closeInviteErrorModal() {
                this.isInviteErrorModalOpen = false
            },

            invitesSentAfterEmailCheck(ev) {
                const invites = ev.emails.map((item) => item)
                const data = {
                    invites,
                    userId: this.data.userId,
                    practiceType: PracticeTypeEnum.PHYSICIAN,
                    practiceId: this.data.practiceId,
                }
                this.data.inviteType === InviteTypeEnum.NETWORK ? this.inviteToNetwork(data) : this.inviteToPractice(data)
            },

            resetEmails() {
                const emails = []
                for (let i = 0; i < 3; ++i) {
                    emails.push({
                        email: '',
                        error: '',
                    })
                }
                return emails
            },

            updatePractice(event) {
                if (event) {
                    this.prefillData({ masterUserId: event.data.masterUserId, practiceId: event.value })
                }
            },

            updatePhysician(event) {
                if (event) {
                    this.prefillData({
                        userId: event.value,
                    })
                }
            },

            sendJoinInvites(invites) {
                if (this.data.inviteType === InviteTypeEnum.PRACTICE) {
                    this.inviteToPractice({
                        practiceType: PracticeTypeEnum.PHYSICIAN,
                        invites,
                        userId: this.data.masterUserId,
                        practiceId: this.data.practiceId,
                    })
                } else {
                    this.inviteToNetwork({
                        practiceType: PracticeTypeEnum.PHYSICIAN,
                        invites,
                        userId: this.data.userId,
                        userRoleId: UserRolesEnum.PHYSICIAN.ID,
                    })
                }
            },
        },

        watch: {
            'data.inviteType'(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.clearForm()
                }
            },
        },
    }
</script>

<style lang="scss">
    @import 'styles/components/admin/_invites-members.scss';
</style>
